<template>
  <article class="anamnesis anamnesis-details">
    <header class="anamnesis__header">
      <h3 class="anamnesis__title">
        {{ isNew ? 'Adicionar nova' : 'Editar' }} anamnese
      </h3>

      <label for="anamnesis_name" class="anamnesis-details__name">
        Nome da anamnese
      </label>

      <div
        @mouseenter="event => customFields.some(f => f.hasAnswer) && showTooltip(event)"
        @mouseleave="event => customFields.some(f => f.hasAnswer) && hideTooltip(event)"
      >
        <input
          v-model="anamneseName"
          id="anamnesis_name"
          :disabled="customFields.some(f => f.hasAnswer)"
          type="text"
          class="form-input anamnesis__name-input"
        >
      </div>

      <span
        v-if="!anamneseName"
        class="form-input-hint"
        style="color: red"
      >
        Campo em branco
      </span>

      <p class="anamnesis-details__description">
        Aqui você tem uma pré-visualização do seu prontuário.
        <br>
        Você pode arrastar para reordenar e adicionar novos campos no seu prontuário.
      </p>
    </header>

    <section class="anamnesis__fields anamnesis-field">
      <draggable
        :list="customFields"
        tag="ul"
        handle=".handle"
        class="anamnesis-field__wrapper"
        key="customFields_lista"
      >
        <li
          v-for="(field, index) in customFields"
          :key="`customField_${index}`"
          :ref="`customField_${index}`"
          class="anamnesis-field__item"
        >
            <div class="handle" :ref="`customField_handler_${index}`">
              <i class="icon icon-apps" />
            </div>

            <div
              :class="field.hasAnswer ? 'anamnesis-field__details--blocked' : ''"
              class="anamnesis-field__details highlight"
              @mouseenter="event => field.hasAnswer && showTooltip(event, field.hasAnswer)"
              @mouseleave="event => field.hasAnswer && hideTooltip(event)"
            >
              <div class="anamnesis-field__section anamnesis-field__section--name">
                <label class="anamnesis-field__section-title">
                  Campo do tipo <strong>{{ field.placeholder }}</strong>

                  <span
                    class="tooltip tooltip-top"
                    :data-tooltip="`Lembre-se de preencher o nome do campo \nabaixo. Na área 'Opções', você pode adicionar \ntambém as opções dos campos do tipo lista, \nmúltipla escolha, ou escolha única.`"
                  >
                    <dr-icon class="v-icon" color="gray" name="info" :key="`tooltip_${index}`" />
                  </span>
                </label>

                <input
                  :id="`customField_name_${index}`"
                  v-model="field.name"
                  type="text"
                  class="form-input anamnesis__name-input anamnesis__name-input--mt"
                  placeholder="Digite aqui o nome do campo"
                >

                <span
                  v-if="!field.name"
                  class="form-input-hint"
                  style="color: red; font-weight: 100"
                >
                  Campo em branco
                </span>

                <!-- Adicionar opções (campos select, radio, checkbox) -->
                <div
                  v-if="
                    (
                      field.type === 'select' ||
                      field.type === 'checkbox' ||
                      field.type === 'radio'
                    ) && field.name
                  "
                  class="anamnesis-field__options"
                >
                  <span class="anamnesis-field__options-title">
                    Adicione pelo menos 2 opções
                  </span>

                  <ul class="anamnesis-field__options-list" v-if="field.options.length > 0">
                    <li
                      v-for="(option, i) in field.options"
                      :key="`${field.type}${index}_list_options_${i}`"
                      class="anamnesis-field__options-list-item"
                    >
                      {{ option }}
                      <i
                        class="icon icon-delete clickable anamnesis-field__remove"
                        @click="field.options.splice(i, 1)"
                      />
                    </li>
                  </ul>

                  <div class="anamnesis-field__options-add">
                    <input
                      v-model="field.tempOption"
                      type="text"
                      class="form-input anamnesis__name-input"
                      @keyup.enter="pushOption(index)"
                    >

                    <button
                      class="btn btn-sm btn-green btn-add"
                      :disabled="!field.tempOption"
                      @click="pushOption(index)"
                    >
                      <i class="icon icon-plus clickable" />
                    </button>
                  </div>
                </div>
                <!-- /Adicionar opções (campos select, radio, checkbox) -->
              </div>

              <div class="anamnesis-field__section anamnesis-field__section--result">
                <span class="anamnesis-field__section-title">
                  Prévia
                </span>

                <label class="form-label" v-show="field.name">
                  {{ field.name }}
                </label>

                <template v-if="field.type === 'text'">
                  <input
                    v-show="field.name"
                    :ref="`field_${field.type}_${index}`"
                    class="form-input"
                    type="text"
                  >
                </template>

                <template v-if="field.type === 'textarea'">
                  <textarea
                    v-show="field.name"
                    :ref="`field_${field.type}_${index}`"
                    class="form-input"
                    cols="10"
                    rows="3"
                  />
                </template>

                <template v-if="field.type === 'number'">
                  <input
                    v-show="field.name"
                    :ref="`field_${field.type}_${index}`"
                    class="form-input"
                    type="number"
                    step="1"
                  >
                </template>

                <template v-if="field.type === 'date'">
                  <div
                    v-click-outside="() => { field.show = false }"
                    @click.self="field.show = !field.show"
                    class="filters__field-datepicker question__field question__field--date"
                  >
                    <input
                      v-show="field.name"
                      :id="`field_${field.type}_${index}`"
                      :value="$moment(field.value).format('DD/MM/YYYY')"
                      class="form-input"
                      type="text"
                      placeholder="Escolha a data"
                      @focus="field.show = true"
                    >
                    <div class="agenda-settings__date-picker--wrapper" style="top: 100%; color: gray">
                      <dr-calendar
                        v-show="field.show"
                        v-model="field.value"
                        @change="field.show = false"
                      />
                    </div>
                  </div>
                </template>

                <template v-if="field.type === 'select'">
                  <select class="form-select" v-show="field.name">
                    <option
                      v-for="(option, i) in field.options"
                      :key="`${field.type}${field.index}_option_${i}`"
                    >
                      {{ option }}
                    </option>
                  </select>
                </template>

                <template v-if="field.type === 'checkbox'">
                  <label
                    v-for="(option, i) in field.options"
                    v-show="field.name"
                    :key="`${field.type}${field.index}_option_${i}`"
                    class="form-checkbox"
                  >
                    <input type="checkbox">

                    <i class="form-icon" />
                    {{ option }}
                  </label>
                </template>

                <template v-if="field.type === 'radio'">
                  <label
                    v-show="field.name"
                    v-for="(option, i) in field.options"
                    :key="`${field.type}${field.index}_option_${i}`"
                    class="form-radio"
                  >
                    <input type="radio" :name="`${field.type}${field.index}_radio`">
                    <i class="form-icon" />
                    {{ option }}
                  </label>
                </template>
              </div>

              <div class="anamnesis-field__section anamnesis-field__section--actions">
                <i
                  v-show="!field.hasAnswer"
                  class="icon icon-delete clickable anamnesis-field__remove"
                  @click="removeField(index)"
                />
              </div>
            </div>
        </li>
      </draggable>

      <nav class="anamnesis-details__add-field">
        <button
          class="btn btn-green btn-sm anamnesis-details__btn-new-field"
          @click="isModalActive = true"
        >
          <i class="icon icon-plus" /> Adicionar novo campo
        </button>
      </nav>
    </section>

    <footer class="anamnesis-details__footer">
      <button
        class="btn btn-blue btn-md mr-2"
        @click="$router.push({ name: 'DoctorModelsAnamnesis' })"
      >
        Voltar
      </button>

      <button
        class="btn btn-primary btn-md"
        :disabled="!isValid"
        @click="submit()"
      >
        Salvar anamnese
      </button>
    </footer>

    <anamnesis-fields
      v-if="isModalActive"
      @close="closeModal()"
      @addField="addField($event)"
    />
  </article>
</template>

<script>
import anamnesisFields from '@/components/layout/models/anamnesisFields'
import draggable from 'vuedraggable'
import { DrCalendar } from 'cm2tech-vue-calendar'

export default {
  name: 'DrAnamnesisDetails',

  components: {
    anamnesisFields,
    draggable,
    DrCalendar
  },

  data () {
    return {
      sending: false,
      id: this.$route.params.id,
      isModalActive: false,
      anamneseName: null,
      customFields: [],
      oldCustomFields: [],
      fieldTypes: [
        { type: 'text', name: 'Campo de texto' },
        { type: 'textarea', name: 'Texto longo' },
        { type: 'number', name: 'Campo numérico' },
        { type: 'select', name: 'Lista' },
        { type: 'checkbox', name: 'Múltipla escolha' },
        { type: 'radio', name: 'Escolha única' },
        { type: 'date', name: 'Data' }
      ]
    }
  },

  computed: {
    isNew () {
      return (this.id === 'new')
    },

    isValid () {
      return (!!this.anamneseName && this.customFieldsLength > 0 && this.validateCustomFields(this.customFields)) && !this.sending
    },

    customFieldsLength () {
      return this.customFields.length
    }
  },

  watch: {
    customFieldsLength (next, prev) {
      this.$nextTick(() => {
        const ref = `customField_${this.customFields.length - 1}`
        const field = this.$refs[ref][0].querySelector('.anamnesis-field__details')

        if (field.classList.contains('highlight') === false) {
          field.scrollIntoView({ block: 'start', behaviour: 'smooth' })
          field.classList.add('highlight')
        }
      })
    }
  },

  created () {
    this.fn_HIDE_PROGRESS()
  },

  mounted () {
    if (!this.isNew) {
      const url = `/doctor/custom_anamnesis/${this.id}`

      this.fn_SHOW_PROGRESS()
      this.$http
        .get(url, this.$store.getters.getAuthHeader)
        .then((res) => {
          this.anamneseName = res.data['name']

          res.data['custom_anamnesis_questions'].forEach((question) => {
            const type = this.fieldTypes
              .find(type => type.type === question['field_type'].toLowerCase())

            const options = question['custom_anamnesis_question_options'].map(option => option.name)
            const { name, id, position } = question
            const hasAnswer = question['has_answer']

            this.addField(type, name, options, id, hasAnswer, position)
          })
        })
        .catch(err => this.fn_HANDLE_ERROR(err))
        .finally(() => {
          this.customFields = this.customFields.sort((a, b) => a.position > b.position)
          this.oldCustomFields = [...this.customFields]
          this.fn_HIDE_PROGRESS()
        })
    }
  },

  methods: {
    closeModal () {
      this.isModalActive = false
    },

    addField (type, name = null, options = [], id = null, hasAnswer = false, position = null) {
      const field = {
        id,
        name,
        options,
        hasAnswer,
        position,
        type: type.type,
        placeholder: type.name,
        show: false,
        value: null
      }

      this.customFields.push(field)
    },

    removeField (index) {
      const removeField = (index, id = null) => {
        return new Promise((resolve, reject) => {
          if (id) {
            const url = `/doctor/custom_anamnesis/${this.id}/custom_anamnesis_question/${id}`

            this.$http
              .delete(url, this.$store.getters.getAuthHeader)
              .then(() => {
                this.customFields.splice(index, 1)
                resolve()
              })
              .catch(err => reject(err))
          } else {
            this.customFields.splice(index, 1)
            resolve()
          }
        })
      }

      this.$store.dispatch('setModalConfirm', {
        title: 'Confirmação',
        body: `Tem certeza que deseja remover o campo da anamnese? Essa ação não poderá ser desfeita.`,
        confirmText: 'Confirmar',
        cancelText: 'Cancelar',
        beforeClose: (v) => {
          this.$store.dispatch('setModalConfirm')

          if (v) {
            this.fn_SHOW_PROGRESS()
            removeField(index, this.customFields[index].id)
              .then(() => {
                this.$store.dispatch('setModalAlert', {
                  title: 'Sucesso',
                  body: 'A questão foi removida da anamnese com sucesso',
                  bsize: 'md',
                  active: true
                })
              })
              .catch(err => this.fn_HANDLE_ERROR(err))
              .finally(this.fn_HIDE_PROGRESS)
          }
        },
        size: 'md',
        active: true
      })
    },

    pushOption (index) {
      const added = this.customFields[index].options.find((option) => {
        return option.localeCompare(
          this.customFields[index].tempOption,
          'pt-BR',
          { sensitivity: 'base' }
        ) === 0
      })

      if (!added) {
        this.customFields[index].options.push(this.customFields[index].tempOption)
        this.customFields[index].tempOption = null
      } else {
        this.$store.dispatch('setModalAlert', {
          title: 'Erro',
          body: 'Você já adicionou essa opção.',
          size: 'md',
          active: true
        })
      }
    },

    validateCustomFields (fields) {
      const areNamed = fields.every(field => !!field.name)
      const fieldsWithOptions = fields.filter(field => field.type === 'select' || field.type === 'checkbox' || field.type === 'radio')
      const haveOptions = fieldsWithOptions.every(field => field.options.length >= 2)

      return areNamed && haveOptions
    },

    submit () {
      const saveAnamnesis = (id = null) => {
        return new Promise((resolve, reject) => {
          if (!this.customFields.some(f => f.hasAnswer)) {
            const url = `/doctor/custom_anamnesis${id ? `/${id}` : ''}`
            const method = id ? 'put' : 'post'
            const headers = this.$store.getters.getAuthHeader.headers
            const data = {
              custom_anamnesis: {
                name: this.anamneseName,
                isActive: true
              }
            }

            this.$http({ url, method, data, headers })
              .then(res => resolve(res))
              .catch(err => reject(err))
          } else {
            console.log('deveria entrar aqui né')
            resolve()
          }
        })
      }

      const saveAnamnesisQuestions = (id) => {
        const fields = []
        this.customFields.forEach((field, index) => {
          const isEditable = field.id &&
            (
              (field.hasAnswer && index !== this.oldCustomFields.findIndex(f => f.id === field.id)) ||
              !field.hasAnswer
            )

          const isNew = !field.hasAnswer && !field.id

          if (isEditable || isNew) {
            const saveField = () => {
              const url = `/doctor/custom_anamnesis/${
                id
              }/custom_anamnesis_question${
                isNew ? '' : `/${field.id}`
              }`

              const method = isNew ? 'post' : 'put'
              const headers = this.$store.getters.getAuthHeader.headers

              const questionOptions = field.options.map(option => ({ name: option }))
              const data = {
                'custom_anamnesis_question': {
                  name: field.name,
                  fieldType: field.type.toUpperCase(),
                  position: index,
                  ...(questionOptions.length > 0 && { customAnamnesisQuestionOptions: questionOptions })
                }
              }

              return this.$http({ url, method, data, headers })
            }

            fields.push(saveField)
          }
        })

        fields.forEach(persist => (persist().catch(err => err)))
        return this.allSettled(fields)
      }

      this.fn_SHOW_PROGRESS()
      this.sending = true

      saveAnamnesis(this.isNew ? null : this.id)
        .then(res => saveAnamnesisQuestions(this.isNew ? res.data.id : this.id))
        .then(() => {
          this.$store.dispatch('setModalAlert', {
            title: 'Sucesso',
            body: `Modelo de anamnese ${this.isNew ? 'criado' : 'editado'} com sucesso.`,
            size: 'md',
            active: true
          })

          this.$router.push({ name: 'DoctorModelsAnamnesisList' })
        })
        .catch(err => this.fn_HANDLE_ERROR(err))
        .finally(() => {
          this.fn_HIDE_PROGRESS()
          this.sending = false
        })
    },

    allSettled (promises) {
      const wrappedPromises = promises
        .map(p => Promise.resolve(p).then(
          val => ({ status: 'fulfilled', value: val }),
          err => ({ status: 'rejected', reason: err })
        ))

      return Promise.all(wrappedPromises)
    },

    showTooltip (event, status = false) {
      const addTooltip = (event) => {
        const position = event.getBoundingClientRect()
        const tooltip = document.createElement('div')

        tooltip.classList.add('agenda-online-controls__tooltip', 'confirmation-tooltip', 'anamnesis__tooltip')
        tooltip.innerText = !status
          ? 'O nome da anamnese não pode ser editado por que ela está vinculada a um ou mais atendimentos.'
          : 'Essa questão não pode ser editada por estar vinculada a um ou mais atendimentos.'
        tooltip.style.display = 'none'
        tooltip.style.position = 'absolute'
        tooltip.style.top = 'calc(0% + 15px)'
        tooltip.style.left = `${(position.width / 2) - 17}px`
        tooltip.style.zIndex = '100'
        tooltip.style.transition = 'all ease 0.5s'

        event.style.position = 'relative'
        event.appendChild(tooltip)

        setTimeout(() => (tooltip.style.display = 'block'), 100)
      }

      addTooltip(event.target)
    },

    hideTooltip (event) {
      const removeTooltip = () => {
        const tooltip = document.querySelectorAll('.confirmation-tooltip')
        if (tooltip.length > 0) {
          tooltip.forEach(t => t.parentNode.removeChild(t))
        }
      }

      removeTooltip()
    }
  }
}
</script>
